import { Injectable } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import {
    CurrentModuleOptions,
    errorPageQueryString,
    ModuleUserAccessLevel,
    PageErrorType,
    PI_VERSION
} from '../constants-types';
import { PI } from '../constants-types/PI.model';
import { InitData, ModuleAccessConfiguration } from '../core-data-model';
import { QueryStringUtility } from '../core-utilities/QueryString.utility';
import { AppType } from '../shared';
import { ConfigurationService } from '../system-configuration';
import {
    ApplicationInitializedMessage,
    DemoDataService,
    FeatureFlags,
    FeatureFlagsService,
    InitDataService,
    MessagingService,
    ModuleAccessTransformer
} from '../system-services';
import { ModuleAccessService } from '../system-services/ModuleAccess/ModuleAccess.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationDataService } from '../translations/Translation.dataservice';
@Injectable({
    providedIn: 'root'
})
export class AppBootstrapHelpersService {
    constructor(
        private translations: TranslationDataService,
        private queryStringUtil: QueryStringUtility,
        private moduleAccess: ModuleAccessService,
        private userProfile: UserProfileSettingsService,
        private initDataService: InitDataService,
        private messagingService: MessagingService,
        private moduleAccessTransform: ModuleAccessTransformer,
        private config: ConfigurationService,
        private featureFlagService: FeatureFlagsService,
        private demoDataService: DemoDataService
    ) {}

    async bootstrapInitDataAndModuleAccess(): Promise<InitData> {
        const initData = await this.buildInitData();
        this.userProfile.setup();
        await this.moduleAccess.fetchConfig();

        this.performRedirectIfPerformOnly();

        this.featureFlagService.initLaunchDarkly(
            this.initDataService,
            this.userProfile,
            this.moduleAccess
        );

        await this.featureFlagService.completionPromise;

        // has to be after the feature flag initialization since it leverages a FF
        this.performRedirectIfReadOnlyUser();

        const isDemoEnabled = this.demoDataService.isDemoEnabled();
        if (isDemoEnabled) {
            await this.demoDataService.applyDemoDataExperience();
        }

        // used by the auth route guards
        this.messagingService.sendMessage(new ApplicationInitializedMessage());

        this.initializeClientAnalytics(
            initData,
            this.moduleAccess.moduleAccessConfig.softwareModuleAccessConfig
        );

        return initData;
    }

    datadogInit(
        applicationId: string,
        clientToken: string,
        appServiceName = 'pi-spa',
        version: string = PI_VERSION
    ) {
        datadogRum.init({
            applicationId,
            clientToken,
            site: 'datadoghq.com',
            service: appServiceName,
            env: PI.appSettings.environmentName,
            sampleRate: 100,
            trackInteractions: false,
            version: version,
            /*
             * We cannot just do *.predictiveindex.com because this will hit our Auth0 tenant domain.
             * Auth0 doesn't like this and it causes users to get logged out repeatedly.
             * Fortunately, we're now pretty consolidated on a single domain for API calls thanks to API Management
             */
            allowedTracingOrigins: [
                // APIM custom domains
                'https://develop-api.predictiveindex.com',
                'https://staging-api.predictiveindex.com',
                'https://api.predictiveindex.com',

                // Classic API custom domains
                'https://develop-classic-surl.predictiveindex.com',
                'https://pi-staging.predictiveindex.com',
                'https://pi.predictiveindex.com'
            ]
        });
    }

    async fetchAndLoadTranslations(): Promise<any> {
        const translationsResponse = await this.fetchTranslations();
        const translationsContent = await translationsResponse.text();
        this.translations.data =
            this.evaluateTranslationsResponse(translationsContent);
    }

    /**
     * This function will load the sprite sheets, append them to the dom, and fetch the translations
     * and load them in the TranslationService.
     *
     * The `baseUrl` can be provided for local development purposes. This is
     * set to the pi-spa URL by default to assist with static apps, and because
     * most of the time, it will be using pi-spa in the associated deployed env.
     * The only time this needs to be provided is with local development in
     * theory, where there will be CORS issues otherwise.
     */
    async loadSpriteSheetsAndTranslations(appName?: AppType): Promise<void> {
        // Parallelize these calls to make them faster
        const [
            translationsContent,
            iconSpriteContent,
            referencePatternContent
        ] = await Promise.all([
            (await this.fetchTranslations()).text(),
            (await this.fetchIconSprite()).text(),
            (await this.fetchReferencePatternSprite()).text()
        ]);

        this.appendSvgStringToDom(iconSpriteContent);
        this.appendSvgStringToDom(referencePatternContent);
        this.translations.data =
            this.evaluateTranslationsResponse(translationsContent);

        if (appName) {
            await this.loadAndAppendSpecificSprite(appName);
        }
    }

    async loadAndAppendSpecificSprite(appName: AppType) {
        // Do the same for app specific icon sprite sheet
        const appSpecificIconSprite =
            await this.fetchAppSpecificIconSprite(appName);
        this.appendSvgStringToDom(await appSpecificIconSprite.text());
    }

    // Adding this here to make it so we don't need to also import config service where
    // this is called
    getLogRocketEnabled(): boolean {
        return this.config.getLogRocketEnabled();
    }

    // If the user is not an appropriate module level admin and is not a third-party user,
    // redirect them to Perform if they made it to PI2.
    private performRedirectIfPerformOnly() {
        if (
            this.moduleAccess.hasOnlyPerform &&
            !this.isPerformAdmin() &&
            !this.userProfile.isUserThirdPartyOrHasSuperAdminAccess()
        ) {
            this.redirectToPerform();
        }
    }

    private redirectToPerform() {
        window.location.assign(
            new URL('/token_auth/pi_auth0_login', PI.appSettings.performUrl)
        );
    }

    private isPerformAdmin(): boolean {
        return (
            this.initDataService.userAccessLevelMap.get(
                CurrentModuleOptions.Perform
            )?.userAccessLevel >= ModuleUserAccessLevel.Admin
        );
    }

    /**
     * Redirects the user to the Perform app if they are a legacy-read-only user and have access to Perform.
     */
    private performRedirectIfReadOnlyUser() {
        if (
            this.moduleAccess.hasPerform &&
            !this.moduleAccess.hasV2ModuleAccess &&
            this.initDataService.isReadOnly() &&
            this.featureFlagService.getByFlag(
                FeatureFlags.enablePerformRedirectForReadOnlyUsers
            )
        ) {
            this.redirectToPerform();
        }
    }

    private initializeClientAnalytics(
        initData: InitData,
        softwareModuleAccessConfig?: Array<ModuleAccessConfiguration>
    ) {
        const config = softwareModuleAccessConfig
            ? this.moduleAccessTransform.configureClientToRum(
                  softwareModuleAccessConfig
              ).softwareModuleAccessRUMConfig
            : null;

        // Add SMAC to InitData
        initData.softwareModuleAccessConfig = config;

        const clientAnalyticsCustomMetadata = {
            // "usr" is a specific DataDog convention:
            // https://docs.datadoghq.com/logs/processing/attributes_naming_convention/#user-related-attributes
            usr: {
                id: initData.administrator.userGuid,
                apiId: initData.administrator.apiUserId,
                email: initData.administrator.email,
                role: initData.administrator.roleName,
                languageLocale: PI.languageLocale // Translations.currentLanguageLocale,
            },
            company: {
                id: initData.administrator.companyId,
                apiId: initData.administrator.apiCompanyId,
                name: initData.administrator.company
            },
            softwareModuleAccessConfig: config
        };

        // Set custom attributes to be included with data sent to analytics platform
        Object.keys(clientAnalyticsCustomMetadata).forEach((key) => {
            datadogRum.addRumGlobalContext(
                key,
                clientAnalyticsCustomMetadata[key]
            );
        });
    }

    private async buildInitData(): Promise<InitData> {
        try {
            return await this.initDataService.getJavaScriptInitData();
        } catch (e) {
            const error: PageErrorType = 'outOfSyncUser';
            window.location.href = `/error?${errorPageQueryString}=${error}`;
        }
    }

    // NOTE: We have a fetch polyfill included so this might be fine
    // The Angular HttpClient was throwing errors because we have a
    // ".json" extension on this endpoint but it's actually javascript.
    // We should make it either have a `.js` extension or make it just
    // actually JSON.
    private async fetchTranslations() {
        // Our code needs to maintain the ability to base language off of
        // query string to make image service work
        let drupalCode = PI.language.drupalCode;

        // initDataService optionally checked due to dependency on image service.
        if (
            this.initDataService.isInitDataLoaded &&
            this.initDataService.account.piVersion === 2
        ) {
            const allowableLanguages = PI.productLanguageMap.FullSite.filter(
                (x) => x.isAvailableForPI2
            ).map((x) => x.languageLocaleCode);

            const userLanguage =
                this.initDataService.profile.defaultLanguageCode;

            const languageCode = allowableLanguages.includes(userLanguage)
                ? userLanguage
                : 'en-US';

            drupalCode = this.getDrupalCode(languageCode);
        }

        const queryLanguage =
            this.queryStringUtil.fetchQueryParameterValue('languageLocale');

        if (queryLanguage) {
            const queryDrupalCode = this.getDrupalCode(queryLanguage);

            if (queryDrupalCode) {
                drupalCode = queryDrupalCode;
            }
        }

        const { translationsCdn, translationVersion } = PI.appSettings;

        const makeUrl = (lang: string) =>
            `${translationsCdn}/translations.${lang}.json?v=${translationVersion}`;

        const translationCode = (drupalCode ?? 'en').toLowerCase();

        let response = await fetch(makeUrl(translationCode), { method: 'GET' });

        if (!response.ok && translationCode !== 'en') {
            response = await fetch(makeUrl('en'), { method: 'GET' });
        }

        return response;
    }

    private getDrupalCode(languageCode): string {
        let drupalCode = '';

        if (languageCode) {
            // Using DoNotConsiderAvailability to always grab the drupalCode if one exists for the language
            // Note: Reports from the Image Service may support more/different languages than the site's general UI
            // eslint-disable-next-line no-underscore-dangle
            const translation = (
                window as any
            ).__productLanguageMap.DoNotConsiderAvailability.find(
                (langInfo) =>
                    languageCode.toLowerCase() ===
                    langInfo.languageLocaleCode.toLowerCase()
            );
            if (translation) {
                drupalCode = translation.drupalCode;
            }
        }

        return drupalCode;
    }

    private fetchIconSprite() {
        return fetch(`/Assets/pi-icon-sprite.svg`, {
            method: 'GET'
        });
    }

    private fetchAppSpecificIconSprite(appName: AppType) {
        /* To load the app specific pi-icon-sprite.svg file we're prefixing the app name.
            For instance, Hire app will need to load /hireAssets/pi-hire-icon-sprite.svg */

        const staticAssetsAppName = AppType[appName].toLowerCase();
        return fetch(
            `/${staticAssetsAppName}Assets/pi-${staticAssetsAppName}-icon-sprite.svg`,
            {
                method: 'GET'
            }
        );
    }

    private fetchReferencePatternSprite() {
        // Define headers so that if the sprite sheet is new on the server,
        // that is pulled down instead of the cached version.
        const headers = new Headers();
        headers.append('pragma', 'no-cache');
        headers.append('cache-control', 'no-cache');

        return fetch(`/Assets/ReferencePatterns/reference-pattern-sprite.svg`, {
            method: 'GET',
            headers: headers
        });
    }

    private evaluateTranslationsResponse(
        translationsResponseText: string
    ): any {
        const translationFilePrefix = 'var Translations=';
        let library: any;
        if (translationsResponseText.startsWith(translationFilePrefix)) {
            library = JSON.parse(
                translationsResponseText.slice(translationFilePrefix.length)
            );
        } else {
            library = JSON.parse(translationsResponseText);
        }
        return {
            ...library,
            key: function (keyName: string) {
                return this[keyName.toLowerCase()];
            }
        };
    }

    private appendSvgStringToDom(svgString: string): void {
        const svgWrapper = document.createElement('div');
        svgWrapper.innerHTML = svgString;
        document.body.appendChild(svgWrapper);
    }
}
